// Copyright (C) 2025, Lifefinder Systems International AB, all rights reserved.
import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { buttonStyle, smallButtonStyle } from '../../../styles/constants';
import type { LfDevice, Beacon, CheckinStation } from '../../../types';
import {
  beaconAPI,
  checkinAPI,
  lfDeviceAPI,
  deviceAPI,
  vsmAPI,
  type VsmSchemaEntry,
} from '../../../utils/apiClient';
import {
  LORA_PORT_DOTS,
  LORA_PORT_BEACON,
  LORA_PORT_PERSISTENT,
  generateConfigDownlink,
  generatePersistentDownlink,
  parseVsmSchema,
  encodeDotsField,
  toHexString,
  toBase64,
  type VsmField,
  type BeaconConfig,
  type DownlinkResult,
} from '../../../utils/downlinkGenerator';
import logger from '../../../utils/logger';

// ============================================================================
// Types
// ============================================================================

type Section = 'lf_device' | 'lf_beacon' | 'lf_checkin';

interface FieldState {
  enabled: boolean;
  value: string;
}

interface SendResult {
  deviceId: string;
  deviceName: string;
  port: number;
  success: boolean;
  error?: string;
}

interface DeviceItem {
  id: string; // deveui used as downlink target
  name: string;
}

export interface LoraConfigProps {
  onClose: () => void;
  inline?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const SECTION_LABELS: Record<Section, string> = {
  lf_device: 'LF Devices',
  lf_beacon: 'Beacons',
  lf_checkin: 'Check-ins',
};

const BEACON_FIELDS: {
  key: keyof BeaconConfig;
  label: string;
  type: 'text' | 'number' | 'select';
  placeholder?: string;
  options?: string[];
}[] = [
  { key: 'ssid', label: 'SSID', type: 'text', placeholder: 'NetworkName' },
  { key: 'password', label: 'Password', type: 'text', placeholder: 'password (8-63 chars)' },
  { key: 'channel', label: 'Channel', type: 'number', placeholder: '1-13' },
  { key: 'power', label: 'Output Power (dBm)', type: 'number', placeholder: '0-26' },
  { key: 'interval', label: 'Beacon Interval (ms)', type: 'number', placeholder: '40-1000' },
  { key: 'jitter', label: 'Jitter (ms)', type: 'number', placeholder: '0-10' },
  {
    key: 'rate',
    label: 'Data Rate',
    type: 'text',
    placeholder: '1/2/6/9/12/18/24/36/48/54/MCS0-5',
  },
  { key: 'protocol', label: 'Protocol', type: 'text', placeholder: 'B/G/N/BG/BGN' },
  { key: 'enabled', label: 'Beacon Enabled', type: 'select', options: ['true', 'false'] },
];

// ============================================================================
// Helpers
// ============================================================================

function buildBeaconFieldStates(): Record<string, FieldState> {
  const states: Record<string, FieldState> = {};
  for (const f of BEACON_FIELDS) {
    states[f.key] = { enabled: false, value: f.options ? f.options[0] : '' };
  }
  return states;
}

// ============================================================================
// Sub-components
// ============================================================================

interface GroupProps {
  crcKey: string;
  appName: string | null;
  items: DeviceItem[];
  selected: Set<string>;
  onToggleDevice: (id: string, crc: string) => void;
  onToggleGroup: (ids: string[], crc: string) => void;
}

const DeviceGroup: React.FC<GroupProps> = ({
  crcKey,
  appName,
  items,
  selected,
  onToggleDevice,
  onToggleGroup,
}) => {
  const allSelected = items.every((i) => selected.has(i.id));
  const someSelected = items.some((i) => selected.has(i.id));
  const ids = items.map((i) => i.id);

  return (
    <div
      style={{
        marginBottom: '1.5vmin',
        border: '1px solid #ccc',
        borderRadius: '0.5vmin',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: '#f0f4ff',
          padding: '0.8vmin 1vmin',
          display: 'flex',
          alignItems: 'center',
          gap: '1vmin',
          borderBottom: '1px solid #ccc',
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected && !allSelected;
          }}
          onChange={() => onToggleGroup(ids, crcKey)}
          style={{ width: '1.6vmin', height: '1.6vmin', cursor: 'pointer' }}
        />
        <span style={{ fontSize: 'max(12px, 1.5vmin)', fontWeight: 600, color: '#333' }}>
          {appName || 'Unknown app'}
        </span>
        <span style={{ fontSize: 'max(10px, 1.2vmin)', color: '#888', fontFamily: 'monospace' }}>
          CRC: {crcKey === 'unknown' ? 'N/A' : crcKey}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 'max(10px, 1.2vmin)', color: '#555' }}>
          {items.length} device{items.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ padding: '0.5vmin 1vmin' }}>
        {items.map((item) => (
          <label
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8vmin',
              padding: '0.4vmin 0',
              cursor: 'pointer',
              fontSize: 'max(12px, 1.5vmin)',
            }}
          >
            <input
              type="checkbox"
              checked={selected.has(item.id)}
              onChange={() => onToggleDevice(item.id, crcKey)}
              style={{ width: '1.4vmin', height: '1.4vmin', cursor: 'pointer' }}
            />
            <span style={{ flex: 1 }}>{item.name}</span>
            <span
              style={{ fontFamily: 'monospace', fontSize: 'max(10px, 1.2vmin)', color: '#888' }}
            >
              {item.id}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Dynamic Dots Config Form (schema-driven)
// ============================================================================

interface DotsConfigFormProps {
  fields: VsmField[];
  states: Record<string, FieldState>;
  onChange: (name: string, patch: Partial<FieldState>) => void;
  usePersistent: boolean;
  onTogglePersistent: () => void;
}

const DotsConfigForm: React.FC<DotsConfigFormProps> = ({
  fields,
  states,
  onChange,
  usePersistent,
  onTogglePersistent,
}) => {
  if (fields.length === 0) {
    return (
      <div style={{ color: '#888', fontSize: 'max(11px, 1.3vmin)', fontStyle: 'italic' }}>
        No schema available — select a device group with a known schema.
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          fontWeight: 600,
          fontSize: 'max(12px, 1.5vmin)',
          color: '#444',
          marginBottom: '0.8vmin',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5vmin',
        }}
      >
        <span>App Settings (Port {usePersistent ? LORA_PORT_PERSISTENT : LORA_PORT_DOTS})</span>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4vmin',
            fontSize: 'max(11px, 1.3vmin)',
            fontWeight: 400,
            color: usePersistent ? '#2ecc40' : '#888',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={usePersistent}
            onChange={onTogglePersistent}
            style={{ cursor: 'pointer' }}
          />
          Persistent (port {LORA_PORT_PERSISTENT})
        </label>
      </div>
      {fields.map((field) => {
        const state = states[field.name] ?? { enabled: false, value: '' };
        return (
          <div
            key={field.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8vmin',
              padding: '0.3vmin 0',
              fontSize: 'max(12px, 1.5vmin)',
            }}
          >
            <input
              type="checkbox"
              checked={state.enabled}
              onChange={(e) => onChange(field.name, { enabled: e.target.checked })}
              style={{ width: '1.4vmin', height: '1.4vmin', cursor: 'pointer', flexShrink: 0 }}
            />
            <span
              style={{
                minWidth: '20vmin',
                color: state.enabled ? '#000' : '#999',
                fontFamily: 'monospace',
                fontSize: 'max(11px, 1.4vmin)',
              }}
            >
              {field.name}
            </span>
            <input
              type="number"
              value={state.value}
              disabled={!state.enabled}
              step={field.translate !== 1 ? field.translate : 1}
              onChange={(e) => onChange(field.name, { value: e.target.value })}
              style={{
                width: '8vmin',
                padding: '0.2vmin 0.4vmin',
                fontSize: 'max(11px, 1.4vmin)',
                borderRadius: '0.3vmin',
                border: '1px solid #ccc',
                background: state.enabled ? '#fff' : '#f5f5f5',
                color: state.enabled ? '#000' : '#999',
              }}
            />
            {field.translate !== 1 && (
              <span style={{ color: '#888', fontSize: 'max(10px, 1.2vmin)' }}>
                ×{field.translate}
              </span>
            )}
            <span
              style={{
                color: '#aaa',
                fontSize: 'max(10px, 1.1vmin)',
                fontFamily: 'monospace',
              }}
            >
              id:{field.id}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// ESP32 Beacon Config Form (Port 48) — fixed protocol, not schema-driven
// ============================================================================

interface BeaconConfigFormProps {
  states: Record<string, FieldState>;
  onChange: (key: string, patch: Partial<FieldState>) => void;
}

const BeaconConfigForm: React.FC<BeaconConfigFormProps> = ({ states, onChange }) => (
  <div>
    <div
      style={{
        fontWeight: 600,
        fontSize: 'max(12px, 1.5vmin)',
        color: '#444',
        marginBottom: '0.8vmin',
      }}
    >
      ESP32 Beacon Settings (Port {LORA_PORT_BEACON})
    </div>
    {BEACON_FIELDS.map((field) => {
      const state = states[field.key] ?? { enabled: false, value: '' };
      return (
        <div
          key={field.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.8vmin',
            padding: '0.3vmin 0',
            fontSize: 'max(12px, 1.5vmin)',
          }}
        >
          <input
            type="checkbox"
            checked={state.enabled}
            onChange={(e) => onChange(field.key, { enabled: e.target.checked })}
            style={{ width: '1.4vmin', height: '1.4vmin', cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ minWidth: '20vmin', color: state.enabled ? '#000' : '#999' }}>
            {field.label}
          </span>
          {field.type === 'select' ? (
            <select
              value={state.value}
              disabled={!state.enabled}
              onChange={(e) => onChange(field.key, { value: e.target.value })}
              style={{
                padding: '0.2vmin 0.4vmin',
                fontSize: 'max(11px, 1.4vmin)',
                borderRadius: '0.3vmin',
                border: '1px solid #ccc',
                background: state.enabled ? '#fff' : '#f5f5f5',
                color: state.enabled ? '#000' : '#999',
              }}
            >
              {field.options!.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={field.type}
              value={state.value}
              disabled={!state.enabled}
              placeholder={field.placeholder}
              onChange={(e) => onChange(field.key, { value: e.target.value })}
              style={{
                width: '12vmin',
                padding: '0.2vmin 0.4vmin',
                fontSize: 'max(11px, 1.4vmin)',
                borderRadius: '0.3vmin',
                border: '1px solid #ccc',
                background: state.enabled ? '#fff' : '#f5f5f5',
                color: state.enabled ? '#000' : '#999',
              }}
            />
          )}
        </div>
      );
    })}
  </div>
);

// ============================================================================
// Main LoraConfig Component
// ============================================================================

export const LoraConfig: React.FC<LoraConfigProps> = ({ onClose, inline }) => {
  const { isSuperAdmin: isSuperAdminUser } = useAuth();

  const [section, setSection] = useState<Section>('lf_device');
  const [lfDevices, setLfDevices] = useState<LfDevice[]>([]);
  const [beacons, setBeacons] = useState<Beacon[]>([]);
  const [checkins, setCheckins] = useState<CheckinStation[]>([]);
  const [vsmSchemas, setVsmSchemas] = useState<Record<string, VsmSchemaEntry>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // CRC key of the group currently being configured; null when nothing is selected
  const [selectedCrc, setSelectedCrc] = useState<string | null>(null);
  // When true, use port 7 persistent downlinks; when false, use port 2 debug downlinks
  const [usePersistent, setUsePersistent] = useState(true);

  // Per-field form state keyed by field name — shared across all groups in the section
  const [dotsStates, setDotsStates] = useState<Record<string, FieldState>>({});
  const [beaconStates, setBeaconStates] =
    useState<Record<string, FieldState>>(buildBeaconFieldStates);

  // Downlink generation
  const [downlinkError, setDownlinkError] = useState<string | null>(null);
  const [generatedDownlinks, setGeneratedDownlinks] = useState<DownlinkResult[]>([]);

  // Send state
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState<SendResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    setSelected(new Set());
    setSelectedCrc(null);
    setDotsStates({});
    setSendResults([]);
    setShowResults(false);
  }, [section]);

  // Clear selectedCrc when all devices are deselected
  useEffect(() => {
    if (selected.size === 0) setSelectedCrc(null);
  }, [selected]);

  const loadAll = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [devData, beaconData, checkinData, schemaData] = await Promise.all([
        lfDeviceAPI.getLfDevices(false),
        beaconAPI.getBeacons(),
        checkinAPI.getCheckins(),
        vsmAPI.getSchemas(),
      ]);
      if (devData.success) setLfDevices(devData.devices || []);
      if (beaconData.success) setBeacons(beaconData.beacons || []);
      if (checkinData.success) setCheckins(checkinData.checkins || []);
      if (schemaData.success) setVsmSchemas(schemaData.schemas || {});
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load devices');
      logger.error('LoraConfig: failed to load devices', err);
    } finally {
      setLoading(false);
    }
  };

  // Flat device list for the active section (id + name only)
  const deviceItems = useMemo((): DeviceItem[] => {
    if (section === 'lf_device') {
      return lfDevices.map((d) => ({ id: d.deveui, name: d.name || d.deveui }));
    }
    if (section === 'lf_beacon') {
      return beacons.map((b) => ({ id: b.deveui ?? b.device_id, name: b.name || b.device_id }));
    }
    return checkins.map((c) => ({ id: c.deveui ?? c.device_id, name: c.name || c.device_id }));
  }, [section, lfDevices, beacons, checkins]);

  // Group devices by CRC using the VSM schema map as the authority.
  // Devices not present in any schema group fall into an 'unknown' bucket.
  const grouped = useMemo((): Map<string, DeviceItem[]> => {
    const deviceSet = new Set(deviceItems.map((d) => d.id));
    const groups = new Map<string, DeviceItem[]>();

    for (const [crc, entry] of Object.entries(vsmSchemas)) {
      const members = entry.devices
        .filter((deveui) => deviceSet.has(deveui))
        .map((deveui) => deviceItems.find((d) => d.id === deveui)!)
        .filter(Boolean);
      if (members.length > 0) groups.set(crc, members);
    }

    // Devices with no schema entry yet
    const grouped_ids = new Set(Object.values(vsmSchemas).flatMap((e) => e.devices));
    const ungrouped = deviceItems.filter((d) => !grouped_ids.has(d.id));
    if (ungrouped.length > 0) groups.set('unknown', ungrouped);

    return groups;
  }, [deviceItems, vsmSchemas]);

  // Show schema only for the currently-selected CRC group
  const activeSchema = useMemo((): VsmField[] => {
    if (!selectedCrc || selectedCrc === 'unknown') return [];
    const entry = vsmSchemas[selectedCrc];
    if (!entry?.schema) return [];
    return parseVsmSchema(entry.schema);
  }, [selectedCrc, vsmSchemas]);

  // Regenerate downlinks whenever form state or schema changes
  useEffect(() => {
    regenerateDownlinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotsStates, beaconStates, activeSchema, section, selectedCrc, usePersistent]);

  const toggleDevice = useCallback(
    (id: string, crc: string) => {
      // Switching to a different group → clear current selection first
      if (selectedCrc !== null && crc !== selectedCrc) {
        setSelected(new Set([id]));
        setSelectedCrc(crc);
        setDotsStates({});
        return;
      }
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      setSelectedCrc(crc);
    },
    [selectedCrc]
  );

  const toggleGroup = useCallback(
    (ids: string[], crc: string) => {
      // Switching to a different group → replace selection entirely
      if (selectedCrc !== null && crc !== selectedCrc) {
        setSelected(new Set(ids));
        setSelectedCrc(crc);
        setDotsStates({});
        return;
      }
      setSelected((prev) => {
        const next = new Set(prev);
        const allIn = ids.every((id) => next.has(id));
        if (allIn) ids.forEach((id) => next.delete(id));
        else ids.forEach((id) => next.add(id));
        return next;
      });
      setSelectedCrc(crc);
    },
    [selectedCrc]
  );

  const updateDotsState = useCallback((name: string, patch: Partial<FieldState>) => {
    setDotsStates((prev) => ({
      ...prev,
      [name]: { ...(prev[name] ?? { enabled: false, value: '' }), ...patch },
    }));
  }, []);

  const updateBeaconState = useCallback((key: string, patch: Partial<FieldState>) => {
    setBeaconStates((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const regenerateDownlinks = useCallback(() => {
    setDownlinkError(null);
    const results: DownlinkResult[] = [];
    try {
      // Dots app settings — port 7 (persistent) or port 2 (debug), schema-driven
      const enabledValues: Record<string, number> = {};
      for (const field of activeSchema) {
        const state = dotsStates[field.name];
        if (!state?.enabled) continue;
        const val = parseFloat(state.value);
        if (isNaN(val)) continue;
        enabledValues[field.name] = val;
      }

      if (Object.keys(enabledValues).length > 0) {
        if (usePersistent && selectedCrc && selectedCrc !== 'unknown') {
          results.push(
            generatePersistentDownlink(Number(selectedCrc), activeSchema, enabledValues)
          );
        } else {
          // Port 2 debug (non-persistent)
          const dotsBytes: number[] = [];
          for (const field of activeSchema) {
            if (enabledValues[field.name] === undefined) continue;
            dotsBytes.push(...encodeDotsField(field, enabledValues[field.name]));
          }
          if (dotsBytes.length > 0) {
            const hex = toHexString(dotsBytes, '');
            results.push({
              port: LORA_PORT_DOTS,
              bytes: dotsBytes,
              hex: toHexString(dotsBytes),
              hexNoSpaces: hex,
              base64: toBase64(dotsBytes),
              description: `Port ${LORA_PORT_DOTS}: ${hex}`,
              vsm_config: { ...enabledValues },
            });
          }
        }
      }

      // Port 48 — ESP32 beacon settings (beacons and checkins only)
      if (section !== 'lf_device') {
        const beaconConfig: BeaconConfig = {};
        for (const field of BEACON_FIELDS) {
          const state = beaconStates[field.key];
          if (!state?.enabled || !state.value) continue;
          if (field.key === 'enabled') {
            beaconConfig.enabled = state.value === 'true';
          } else if (field.type === 'number') {
            (beaconConfig as Record<string, unknown>)[field.key] = parseFloat(state.value);
          } else {
            (beaconConfig as Record<string, unknown>)[field.key] = state.value;
          }
        }
        if (Object.keys(beaconConfig).length > 0) {
          beaconConfig.apply = true;
          results.push(generateConfigDownlink(beaconConfig));
        }
      }

      setGeneratedDownlinks(results);
    } catch (err) {
      setDownlinkError(err instanceof Error ? err.message : String(err));
      setGeneratedDownlinks([]);
    }
  }, [dotsStates, beaconStates, activeSchema, section, selectedCrc, usePersistent]);

  const handleSend = async () => {
    if (generatedDownlinks.length === 0 || selected.size === 0) return;

    setSending(true);
    setSendResults([]);
    setShowResults(true);

    const results: SendResult[] = [];
    for (const deviceId of selected) {
      const deviceName = deviceItems.find((d) => d.id === deviceId)?.name || deviceId;
      for (const dl of generatedDownlinks) {
        try {
          await deviceAPI.downlink(deviceId, dl.port, dl.hexNoSpaces, dl.vsm_config);
          results.push({ deviceId, deviceName, port: dl.port, success: true });
          logger.debug(`LoraConfig: sent port ${dl.port} to ${deviceId}`);
        } catch (err) {
          const error = err instanceof Error ? err.message : String(err);
          results.push({ deviceId, deviceName, port: dl.port, success: false, error });
          logger.error(`LoraConfig: port ${dl.port} failed for ${deviceId}: ${error}`);
        }
      }
    }

    setSendResults(results);
    setSending(false);
  };

  if (!isSuperAdminUser) {
    return (
      <div style={{ padding: '4vmin', textAlign: 'center', color: '#c00' }}>
        Access denied — SuperAdmin only.
      </div>
    );
  }

  const selectedCount = selected.size;
  const totalOps = selectedCount * generatedDownlinks.length;
  const canSend = selectedCount > 0 && generatedDownlinks.length > 0 && !sending;

  return (
    <div
      style={
        inline
          ? {}
          : {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.6)',
              zIndex: 4000,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              overflow: 'auto',
              padding: '2vmin',
              boxSizing: 'border-box',
            }
      }
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '1vmin',
          width: '100%',
          ...(inline ? {} : { maxWidth: '900px', minHeight: '80vh' }),
          boxShadow: inline ? 'none' : '0 4px 24px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '2vmin',
            borderBottom: '2px solid #ddd',
            flexShrink: 0,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'max(16px, 2.5vmin)' }}>LoRa Board Configuration</h2>
          {!inline && (
            <button
              onClick={onClose}
              style={{
                ...buttonStyle,
                background: '#f44',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'max(14px, 2vmin)',
              }}
            >
              Close
            </button>
          )}
        </div>

        {/* Section tabs */}
        <div
          style={{
            display: 'flex',
            gap: '0.5vmin',
            padding: '1.5vmin 2vmin 0',
            borderBottom: '1px solid #ddd',
            flexShrink: 0,
          }}
        >
          {(['lf_device', 'lf_beacon', 'lf_checkin'] as Section[]).map((s) => (
            <button
              key={s}
              onClick={() => setSection(s)}
              style={{
                ...smallButtonStyle,
                background: section === s ? '#4A90E2' : '#eee',
                color: section === s ? '#fff' : '#333',
                borderBottom: section === s ? '2px solid #4A90E2' : '2px solid transparent',
                borderRadius: '0.5vmin 0.5vmin 0 0',
                fontWeight: section === s ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {SECTION_LABELS[s]}
            </button>
          ))}
        </div>

        {/* Main content — left: device list, right: config + preview + send */}
        <div
          style={
            inline
              ? { display: 'flex', alignItems: 'flex-start' }
              : { display: 'flex', flex: 1, overflow: 'hidden' }
          }
        >
          {/* Left: Device selection */}
          <div
            style={{
              flex: '0 0 38%',
              borderRight: '1px solid #ddd',
              padding: '1.5vmin',
              ...(inline ? {} : { overflowY: 'auto' }),
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1vmin',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 'max(13px, 1.6vmin)' }}>
                Select Devices
              </span>
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  style={{
                    ...smallButtonStyle,
                    cursor: 'pointer',
                    fontSize: 'max(10px, 1.2vmin)',
                  }}
                >
                  Clear ({selected.size})
                </button>
              )}
            </div>

            {loading && (
              <div style={{ color: '#888', fontSize: 'max(12px, 1.5vmin)', padding: '1vmin' }}>
                Loading...
              </div>
            )}
            {loadError && (
              <div style={{ color: '#c00', fontSize: 'max(12px, 1.5vmin)', padding: '1vmin' }}>
                {loadError}
              </div>
            )}
            {!loading && deviceItems.length === 0 && (
              <div style={{ color: '#888', fontSize: 'max(12px, 1.5vmin)', padding: '1vmin' }}>
                No {SECTION_LABELS[section]} found.
              </div>
            )}

            {Array.from(grouped.entries()).map(([crcKey, items]) => (
              <DeviceGroup
                key={crcKey}
                crcKey={crcKey}
                appName={vsmSchemas[crcKey]?.appName ?? null}
                items={items}
                selected={selected}
                onToggleDevice={toggleDevice}
                onToggleGroup={toggleGroup}
              />
            ))}
          </div>

          {/* Right: Config forms + preview + send */}
          <div
            style={{
              flex: 1,
              padding: '1.5vmin',
              ...(inline ? {} : { overflowY: 'auto' }),
              minWidth: 0,
            }}
          >
            {/* Dots config form — schema-driven */}
            <DotsConfigForm
              fields={activeSchema}
              states={dotsStates}
              onChange={updateDotsState}
              usePersistent={usePersistent}
              onTogglePersistent={() => setUsePersistent((p) => !p)}
            />

            {/* ESP32 beacon config form — beacons and checkins only */}
            {section !== 'lf_device' && (
              <div
                style={{ marginTop: '2vmin', paddingTop: '1.5vmin', borderTop: '1px solid #eee' }}
              >
                <BeaconConfigForm states={beaconStates} onChange={updateBeaconState} />
              </div>
            )}

            {/* Downlink preview */}
            <div style={{ marginTop: '2vmin', paddingTop: '1.5vmin', borderTop: '1px solid #eee' }}>
              <div
                style={{ fontWeight: 600, fontSize: 'max(12px, 1.5vmin)', marginBottom: '0.8vmin' }}
              >
                Downlink Preview
              </div>

              {downlinkError && (
                <div
                  style={{
                    color: '#c00',
                    background: '#fff0f0',
                    border: '1px solid #fcc',
                    borderRadius: '0.3vmin',
                    padding: '0.6vmin',
                    fontSize: 'max(11px, 1.3vmin)',
                    marginBottom: '0.8vmin',
                  }}
                >
                  {downlinkError}
                </div>
              )}

              {generatedDownlinks.length === 0 && !downlinkError && (
                <div style={{ color: '#888', fontSize: 'max(11px, 1.3vmin)' }}>
                  Enable fields above to generate a downlink.
                </div>
              )}

              {generatedDownlinks.map((dl) => (
                <div
                  key={dl.port}
                  style={{
                    background: '#f5f9ff',
                    border: '1px solid #cde',
                    borderRadius: '0.4vmin',
                    padding: '0.8vmin',
                    marginBottom: '0.5vmin',
                    fontSize: 'max(11px, 1.3vmin)',
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#4A90E2', marginBottom: '0.3vmin' }}>
                    Port {dl.port} — {dl.bytes.length} byte{dl.bytes.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', color: '#222' }}>
                    {dl.hex}
                  </div>
                  <div style={{ color: '#666', marginTop: '0.3vmin' }}>
                    Base64: <span style={{ fontFamily: 'monospace' }}>{dl.base64}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Send */}
            <div style={{ marginTop: '2vmin', paddingTop: '1.5vmin', borderTop: '1px solid #eee' }}>
              <button
                onClick={handleSend}
                disabled={!canSend}
                style={{
                  ...buttonStyle,
                  background: canSend ? '#2ecc40' : '#aaa',
                  color: '#fff',
                  border: 'none',
                  cursor: canSend ? 'pointer' : 'not-allowed',
                  fontSize: 'max(13px, 1.8vmin)',
                  fontWeight: 600,
                  padding: '1.2vmin 2.5vmin',
                }}
              >
                {sending
                  ? 'Sending...'
                  : selectedCount === 0
                    ? 'Select devices to send'
                    : generatedDownlinks.length === 0
                      ? 'Configure a setting to send'
                      : `Send to ${selectedCount} device${selectedCount !== 1 ? 's' : ''} (${totalOps} downlink${totalOps !== 1 ? 's' : ''})`}
              </button>
            </div>

            {/* Send results */}
            {showResults && sendResults.length > 0 && (
              <div
                style={{
                  marginTop: '1.5vmin',
                  maxHeight: '20vmin',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '0.4vmin',
                }}
              >
                <div
                  style={{
                    padding: '0.6vmin 1vmin',
                    background: '#f5f5f5',
                    borderBottom: '1px solid #ddd',
                    fontSize: 'max(11px, 1.3vmin)',
                    fontWeight: 600,
                  }}
                >
                  Results ({sendResults.filter((r) => r.success).length}/{sendResults.length}{' '}
                  successful)
                </div>
                {sendResults.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5vmin',
                      padding: '0.4vmin 1vmin',
                      fontSize: 'max(11px, 1.3vmin)',
                      borderBottom: '1px solid #eee',
                      background: r.success ? '#f0fff4' : '#fff0f0',
                    }}
                  >
                    <span>{r.success ? '✓' : '✗'}</span>
                    <span style={{ flex: 1 }}>{r.deviceName}</span>
                    <span style={{ color: '#888' }}>port {r.port}</span>
                    {r.error && (
                      <span style={{ color: '#c00', fontSize: 'max(10px, 1.1vmin)' }}>
                        {r.error}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoraConfig;
